import {kml, gpx} from '@tmcw/togeojson'
import ExifReader from 'exifreader'
import JSZip from 'jszip'
import FitParser from 'fit-file-parser'

function getImageGeometry(gps) {
  let latitude = gps.Latitude || gps.GPSLatitude.description
  let longitude = gps.Longitude || gps.GPSLongitude.description

  if (gps.GPSLatitudeRef && gps.GPSLatitudeRef.value[0] === 'S') {
    latitude = -latitude
  }

  if (gps.GPSLongitudeRef && gps.GPSLongitudeRef.value[0] === 'W') {
    longitude = -longitude
  }

  latitude = Math.max(-90, Math.min(90, latitude))
  longitude = Math.max(-180, Math.min(180, longitude))

  const geometry = {
    type: 'Point',
    coordinates: [
      longitude,
      latitude
    ]
  }

  return geometry
}

function parseDate(s) {
  const b = s.split(/\D/)
  return new Date(b[0], b[1]-1, b[2], b[3], b[4], b[5])
}

function parseXML(data) {
  try {
    const cleanData = data.replace(/xsi:schemaLocation=["'][^"']*["']\s*/g, '')

    const parser = new DOMParser()
    return parser.parseFromString(cleanData, 'application/xml')
  } catch (e) {
    console.error('Error parsing XML data:', e)
    return null
  }
}

export async function readFile(file, readAsBuffer = false) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (event) => {
      resolve(event.target.result)
    }

    reader.onerror = (event) => {
      reject(event.target.error)
    }

    if (readAsBuffer) {
      return reader.readAsText(file)
    }
    return reader.readAsArrayBuffer(file)
  })
}

export const parseKML = async (file) => {
  const contents = await readFile(file, true)
  const xml = parseXML(contents)

  try {
    const featureCollection = kml(xml)
    const features = featureCollection.features.map(feature => {
      // Preserve all original properties from the KML
      feature.properties = {
        ...feature.properties, // Keep all existing properties including description
        // Only set name as fallback if it doesn't exist
        name: feature.properties?.name || feature.properties?.Name || file.name
      }
      return feature
    })
    return {
      collection: {
        type: 'FeatureCollection',
        features: features
      },
      name: file.name
    }
  } catch (error) {
    console.error("Error parsing .kml file:", error)
  }
}

export const parseKMZ = async (file) => {
  try {
    const zip = new JSZip()
    const archive = await zip.loadAsync(file)
    const kmlFiles = Object.keys(archive.files).filter(f => f.endsWith('.kml'))

    if (kmlFiles.length === 0) {
      console.warn("No KML files found in KMZ archive:", file.name)
      return null
    }

    for (let kmlFileName of kmlFiles) {
      const kmlFile = archive.files[kmlFileName]
      const kmlContents = await kmlFile.async('string')
      const xml = parseXML(kmlContents)

      if (!xml) {
        console.warn("Failed to parse XML from KML file:", kmlFileName)
        continue
      }

      try {
        const featureCollection = kml(xml)
        const features = featureCollection.features.map(feature => {
          // Preserve all original properties from the KMZ
          feature.properties = {
            ...feature.properties, // Keep all existing properties including description
            // Only set name as fallback if it doesn't exist
            name: feature.properties?.name || feature.properties?.Name || file.name
          }
          return feature
        })

        return {
          collection: {
            type: 'FeatureCollection',
            features: features
          },
          name: file.name
        }
      } catch (kmlError) {
        console.error("Error parsing KML content from file:", kmlFileName, kmlError)
        continue
      }
    }

    console.error("No valid KML files could be parsed from KMZ:", file.name)
    return null
  } catch (error) {
    console.error("Error parsing .kmz file:", error)
    return null
  }
}

export const parseGeoJSON = async (file) => {
  const contents = await readFile(file, true)
  try {
    const featureCollection = JSON.parse(contents)
    const features = featureCollection.features.map(feature => {
      // Preserve all original properties from the GeoJSON
      feature.properties = {
        ...feature.properties, // Keep all existing properties including description
        // Only set name as fallback if it doesn't exist
        name: feature.properties?.name || feature.properties?.Name || file.name
      }
      return feature
    })

    return {
      collection: {
        type: 'FeatureCollection',
        features: features
      },
      name: file.name
    }
  } catch (error) {
    console.error("Error parsing .geojson file:", error)
  }
}

export const parseImage = async (file) => {
  try {
    const tags = await ExifReader.load(file, { async: true })
    const created_origin = tags.DateTimeOriginal ? parseDate(tags.DateTimeOriginal.value[0]).getTime() : file.lastModified
    const geometry = tags.GPSLatitude && tags.GPSLongitude
      ? getImageGeometry({
          GPSLatitude: tags.GPSLatitude,
          GPSLongitude: tags.GPSLongitude,
          GPSLatitudeRef: tags.GPSLatitudeRef,
          GPSLongitudeRef: tags.GPSLongitudeRef
        })
      : null

    const feature = {
      type: 'Feature',
      properties: {
        name: file.name,
        description: '', // Images don't typically have descriptions, but prepare for it
        created_origin: created_origin,
        type: 'Point',
        resource: file
      },
      geometry: geometry
    }

    return {
      collection: {
        type: 'FeatureCollection',
        features: [feature]
      },
      name: file.name
    }
  } catch (error) {
    console.error("Error parsing image file:", error)
  }
}

export const parseGPX = async (file) => {
  const contents = await readFile(file, true)
  const xml = parseXML(contents)

  try {
    const featureCollection = gpx(xml)
    const features = featureCollection.features.map(feature => {
      // Preserve all original properties from the GPX
      feature.properties = {
        ...feature.properties, // Keep all existing properties including description
        // Only set name as fallback if it doesn't exist
        name: feature.properties?.name || feature.properties?.Name || file.name
      }
      return feature
    })
    return {
      collection: {
        type: 'FeatureCollection',
        features: features
      },
      name: file.name
    }
  } catch (error) {
    console.error("Error parsing .gpx file:", error)
  }
}

export const parseFIT = async (file) => {
  try {
    const buffer = await readFile(file, false)
    const parser = new FitParser({
      force: true,
      speedUnit: 'km/h',
      lengthUnit: 'km',
      temperatureUnit: 'celsius',
      elapsedRecordField: true,
      mode: 'list'
    })

    return new Promise((resolve, reject) => {
      parser.parse(buffer, (error, data) => {
        if (error) {
          console.error("Error parsing .fit file:", error)
          reject(error)
          return
        }

        const features = []
        if (data.records) {
          const coordinates = data.records
            .filter(record => record.position_lat !== undefined && record.position_long !== undefined)
            .map(record => {
              const lat = record.position_lat
              const lng = record.position_long
              return [lng, lat]
            })

          if (coordinates.length > 0) {
            features.push({
              type: 'Feature',
              properties: {
                name: file.name,
                description: '' // FIT files don't typically have descriptions, but prepare for it
              },
              geometry: {
                type: 'LineString',
                coordinates: coordinates
              }
            })
          }
        }

        resolve({
          collection: {
            type: 'FeatureCollection',
            features: features
          },
          name: file.name
        })
      })
    })
  } catch (error) {
    console.error("Error parsing .fit file:", error)
  }
}
